import { type HTMLAttributes, useCallback, useRef } from 'react';

import { type Row } from '@tanstack/react-table';

import { type DataTableOptions } from '@spotify-internal/data-table';

const INTERACTIVE_ROW_TARGET_SELECTOR =
  'button, input, textarea, select, a, [role="button"], [contenteditable]';

const isInteractivePressTarget = (
  pointerType: string,
  pointerTarget: Element | null,
): boolean => {
  const target = ['keyboard', 'virtual'].includes(pointerType)
    ? document.activeElement
    : pointerTarget;
  return Boolean(target?.closest(INTERACTIVE_ROW_TARGET_SELECTOR));
};

export type RowActivationCallbacks<TData> = {
  onRowActivate?: (item: TData, row: Row<TData>) => void;
  onRowOpenInNewTab?: (item: TData, row: Row<TData>) => void;
};

export type DataTableRowActivation<TData> = {
  rowHandlers: Pick<
    DataTableOptions<TData, string>,
    'onRowClick' | 'onRowPress'
  >;
  interactionProps: Pick<HTMLAttributes<HTMLElement>, 'onPointerDownCapture'>;
};

export const useDataTableRowActivation = <TData>(
  callbacks: RowActivationCallbacks<TData>,
): DataTableRowActivation<TData> => {
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;
  const pointerTargetRef = useRef<Element | null>(null);
  const onPointerDownCapture = useCallback<
    NonNullable<HTMLAttributes<HTMLElement>['onPointerDownCapture']>
  >(event => {
    pointerTargetRef.current =
      event.target instanceof Element ? event.target : null;
  }, []);
  const onRowClick = useCallback<
    NonNullable<DataTableOptions<TData, string>['onRowClick']>
  >((_, row) => {
    callbacksRef.current.onRowActivate?.(row.original, row);
  }, []);
  const onRowPress = useCallback<
    NonNullable<DataTableOptions<TData, string>['onRowPress']>
  >((event, row) => {
    if (!event.ctrlKey && !event.metaKey) return;
    if (isInteractivePressTarget(event.pointerType, pointerTargetRef.current))
      return;
    callbacksRef.current.onRowOpenInNewTab?.(row.original, row);
  }, []);
  return {
    rowHandlers: {
      onRowClick: callbacks.onRowActivate ? onRowClick : undefined,
      onRowPress: callbacks.onRowOpenInNewTab ? onRowPress : undefined,
    },
    interactionProps: { onPointerDownCapture },
  };
};
